import type { ReactElement } from 'react';
import { Route, FeatureFlag } from 'config';
import useCurrentUser from 'hooks/useCurrentUser';

import Link from './Link';

type Props = {
  isActive: boolean;
  onClose: () => void;
};

function MobileNav({ isActive, onClose }: Props): ReactElement {
  const { currentUser } = useCurrentUser();

  const {
    isInactive,
  } = currentUser;

  const showFriendbuy = currentUser.hasFeatureEnabled(
    FeatureFlag.FRIENDBUY,
  );
  const isBundlesEnabled = currentUser.hasFeatureEnabled(FeatureFlag.BUNDLES);
  const isHomepageRedesignEnabled = currentUser.hasFeatureEnabled(FeatureFlag.HOMEPAGE_REDESIGN);
  return (
    <>
      <nav
        className={[
          'c-nav-offcanvas',
          isActive ? 'is-active' : '',
        ].join(' ')}
        aria-label="Site navigation"
      >
        {!isInactive && (
          <>
            <div className="c-nav-offcanvas__title">
              My Dashboard
            </div>

            <ul className="c-nav-offcanvas__list">
              <Link
                href={Route.UPCOMING_MENUS}
                data-cy="offcanvas-upcoming-deliveries"
                onClick={onClose}
              >
                Upcoming Deliveries
              </Link>

              <Link
                href={Route.ORDER_HISTORY}
                data-cy="offcanvas-order-history"
                onClick={onClose}
              >
                Order History
              </Link>

              <Link
                href={Route.TRACKING}
                data-cy="offcanvas-track-package"
                onClick={onClose}
              >
                Track Package
              </Link>

              {showFriendbuy && (
                <Link
                  href={Route.REFER}
                  data-cy="offcanvas-refer"
                  onClick={onClose}
                >
                  Refer & Earn
                </Link>
              )}

              <li className="c-nav-offcanvas__divider" />
            </ul>
          </>
        )}

        <div className="c-nav-offcanvas__title">
          Our Product
        </div>

        <ul className="c-nav-offcanvas__list">
          <Link
            href={Route.WEEKLY_MENU_MEALS}
            data-cy="offcanvas-meal-kits"
            reload
          >
            Meal Kits
          </Link>

          <Link
            href={Route.WEEKLY_MENU_READY_TO_EAT}
            data-cy="offcanvas-prepared-meals"
            reload
          >
            Ready To Eat
          </Link>

          <Link
            href={Route.GROCERY_MEALS}
            data-cy="offcanvas-plantry-meals"
            reload
          >
            Grocery
          </Link>

          {isBundlesEnabled && (
            <div className="d-none speero_bundles">
              <Link
                href={Route.BUNDLES}
                data-cy="offcanvas-bundles"
                onClick={onClose}
                reload
              >
                <abbr>
                  Bundles
                </abbr>
              </Link>
            </div>
          )}

          <Link
            href={Route.FROZEN_MEALS}
            data-cy="offcanvas-why-plants"
            reload
          >
            Frozen Meals
          </Link>

          {!isInactive && (
            <Link
              href={Route.WHY_PLANTS}
              data-cy="offcanvas-why-plants"
              reload
            >
              Why Plants
            </Link>
          )}

          {!isInactive && (
            <Link
              href={Route.GIFTS}
              data-cy="offcanvas-gifts"
              reload
            >
              Gifts
            </Link>
          )}

          {isInactive && (
            <Link
              href={Route.PLANS}
              data-cy="offcanvas-plans"
              reload
            >
              {isHomepageRedesignEnabled ? 'Plans' : 'Pricing'}
            </Link>
          )}

          <li className="c-nav-offcanvas__divider" />
        </ul>

        <div className="c-nav-offcanvas__title">
          Help
        </div>

        <ul className="c-nav-offcanvas__list">
          <Link
            href="https://purplecarrot.zendesk.com/hc/en-us"
            data-cy="offcanvas-faqs"
            reload
          >
            <abbr>
              FAQs
            </abbr>
          </Link>

          {!isInactive && (
            <Link
              href="/contacts/new"
              data-cy="offcanvas-contact-us"
              reload
            >
              Contact Us
            </Link>
          )}
        </ul>
      </nav>

      <div
        className={[
          'c-nav-offcanvas-overlay',
          isActive ? 'is-active' : '',
        ].join(' ')}
        role="none"
        onClick={onClose}
        onKeyPress={(e) => e.key === 'Enter' && onClose()}
      />
    </>
  );
}

export default MobileNav;
